import type React from 'react';

import { getLunaSampleUrl } from '@vlabs/api/axios/lp5-client';
import { useGetFace, useGetFaceAttributeSamples } from '@vlabs/api/luna-api/client';
import type { FaceId } from '@vlabs/api/luna-api/model/FaceId';
import type { Similarity } from '@vlabs/api/luna-api/model/Similarity';
import { DetectionIcon } from '@vlabs/icons';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { formatDateTime, DATE_FORMATS } from '@vlabs/shared/utils/date';
import { FaceSample, Tag } from '@vlabs/uikit';

import { GoToFaceDetailsLink } from '@vlabs/monokit/features/go-to-face-details';

import { ListLinkPoint3 } from '../../list-link/ListLink.point3';
import { useEventCardTranslation } from '../i18n';
import st from './shared.module.sass';

interface FaceMatchCardProps {
  faceId: FaceId;
  similarity?: Similarity;
}

function FaceMatchCard({
  faceId,
  similarity = undefined,
}: FaceMatchCardProps): React.JSX.Element | null {
  const { t } = useEventCardTranslation();
  const { data: face } = useGetFace(faceId, { targets: ['face_id', 'lists', 'create_time'] });
  const { data: attributeSamples } = useGetFaceAttributeSamples(faceId);

  if (!face) return null;

  const sampleSrc = getLunaSampleUrl('faces', attributeSamples?.samples[0]);

  return (
    <div
      className={st.Card}
      data-testid="search.result.faceCard"
    >
      <div
        className={st.PrimarySection}
        data-testid="search.result.primarySection"
      >
        <FaceSample
          className={st.Sample}
          src={sampleSrc}
        />
        <div className={st.Details}>
          <Tag
            kind="neutral"
            size="large"
            value={t('match_tag')}
          />
          {similarity != null && (
            <SimilarityTag
              icon={<DetectionIcon />}
              kind="primary"
              size="large"
              title={t('similarity_face')}
              value={similarity}
            />
          )}
          <div
            className={st.Date}
            data-testid="search.result.date"
          >
            {formatDateTime(face.create_time, DATE_FORMATS.withYear)}
          </div>
        </div>
      </div>
      <div
        className={st.SecondarySection}
        data-testid="search.result.secondarySection"
      >
        <div className={st.RowItem}>
          <div
            className={st.RowTitle}
            data-testid="search.result.rowTitle"
          >
            {t('face_id')}
          </div>
          <div className={st.RowValue}>{face.face_id ?? '-'}</div>
        </div>
        <div className={st.RowItem}>
          <div
            className={st.RowTitle}
            data-testid="search.result.rowTitle"
          >
            {t('lists')}
          </div>
          <TruncatedText
            hasTruncation
            tooltipContent={face.lists?.join(', ') ?? ''}
          >
            <ListLinkPoint3 listIds={face.lists} />
          </TruncatedText>
        </div>
      </div>
      {!!face.face_id && (
        <GoToFaceDetailsLink
          className={st.Link}
          data-testid="search.result.detailsLink"
          faceId={face.face_id}
        />
      )}
    </div>
  );
}

export { FaceMatchCard };
